import { clearRegistrationUser } from 'utils/fns';

export interface ValidacaoIniciativa {
  totalOriginal: number;
  totalValidos: number;
  validos: Set<string>; // chaves normalizadas (sem "F"/zeros)
}

export interface ParticipanteEnriquecivel {
  matricula: string;
  confirmado: boolean;
  valido?: boolean;
}

export interface IniciativaEnriquecivel {
  id: string;
  participantes: ParticipanteEnriquecivel[];
  totalValidos?: number;
  totalOriginal?: number;
  /** true somente quando havia registro de validação para a iniciativa
   *  (a collection ECBB cobre só parte das categorias). Quando false, a
   *  validade não pôde ser apurada e não se deve ocultar ninguém por ela. */
  validadeApurada?: boolean;
}

/**
 * Enriquece cada iniciativa com `valido` (por participante) e os totais
 * `totalValidos`/`totalOriginal`.
 *
 * CONTRATO: muta os objetos de entrada in-place (e os retorna por conveniência).
 * Use apenas sobre dados recém-formatados por `formatarIniciativa`/`mapIniciativa`
 * — que produzem objetos novos a cada chamada — e nunca sobre referências
 * compartilhadas/externas que não devam ser alteradas.
 */
export function enriquecerComValidacao<T extends IniciativaEnriquecivel>(
  iniciativas: T[],
  mapa: Map<string, ValidacaoIniciativa>
): T[] {
  iniciativas.forEach((ini) => {
    const registro = mapa.get(ini.id);
    if (!registro) {
      ini.participantes.forEach((p) => {
        p.valido = p.confirmado;
      });
      ini.totalValidos = ini.participantes.filter((p) => p.confirmado).length;
      ini.totalOriginal = ini.participantes.length;
      ini.validadeApurada = false;
      return;
    }
    ini.participantes.forEach((p) => {
      p.valido = registro.validos.has(clearRegistrationUser(p.matricula));
    });
    ini.totalValidos = registro.totalValidos;
    ini.totalOriginal = registro.totalOriginal;
    ini.validadeApurada = true;
  });
  return iniciativas;
}

export interface OpcoesVisibilidade {
  /** Habilita a ocultação de não-válidos neste contexto/card. */
  ativo: boolean;
  validadeApurada?: boolean;
  totalValidos?: number;
  totalOriginal?: number;
}

/**
 * Decide a lista de participantes a exibir e os números do cabeçalho.
 *
 * - Quando `ativo` e a validade foi apurada (`validadeApurada`), oculta os
 *   não-válidos (mostra só `valido === true`) e usa os totais pré-calculados.
 * - Caso contrário (sem validade apurada, ou contexto sem ocultação), mostra
 *   todos os participantes e conta os confirmados — comportamento padrão.
 */
export function participantesVisiveis<T extends { confirmado: boolean; valido?: boolean }>(
  participantes: T[],
  opts: OpcoesVisibilidade
): { lista: T[]; numerador: number; denominador: number } {
  if (opts.ativo && opts.validadeApurada) {
    const lista = participantes.filter((p) => p.valido === true);
    return {
      lista,
      numerador: opts.totalValidos ?? lista.length,
      denominador: opts.totalOriginal ?? participantes.length,
    };
  }
  return {
    lista: participantes,
    numerador: participantes.filter((p) => p.confirmado).length,
    denominador: participantes.length,
  };
}
